import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { formatRoute } from 'react-router-named-routes';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui/icons
import Lock from '@material-ui/icons/Lock';
// @material-ui/core components
import InputAdornment from '@material-ui/core/InputAdornment';
// core components
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
import Button from 'components/UI/CustomButtons/Button';
import CustomInput from 'components/UI/CustomInput/CustomInput';
import GridContainer from 'components/UI/Grid/GridContainer';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import GridItem from 'components/UI/Grid/GridItem';
// our components
import Loading from 'components/common/Loading';
import { USER } from 'routes/paths';
import AuthContext from 'helpers/authContext';
import RouterContext from 'helpers/routerContext';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translateValidationSchema = t =>
  Yup.object({
    password1: Yup.string('')
      .min(6, t('errors.pass_length'))
      .required(t('errors.pass')),
    password2: Yup.string(t('errors.pass'))
      .required(t('errors.pass_confirm'))
      .oneOf([Yup.ref('password1')], t('errors.match')),
  });

/**
 * @class
 * @extends React.Component
 */
class UserChangePassword extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      password1: '',
      password2: '',
    };
  }

  /**
   * @description
   * Set server errors with Formik setErrors function.
   *
   * @param {Array} serverErrors - Api errors.
   * @returns {Object} Global form's errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Handle redirect of cancel btn.
   *
   * @param {number} id - Id of the user to details.
   */
  handleCancelBtn = id => {
    const history = this.context;
    history.push(formatRoute(`${USER.DETAILS}#logs`, { id }));
  };

  /**
   * @description
   * Render ChangePassword form using Formik.
   */
  render() {
    const { password1, password2 } = this.state;
    const {
      classes,
      passwordUpdate,
      errors: serverErrors,
      t,
      loading,
    } = this.props;
    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);
    return (
      <GridContainer justify="center">
        <Snackbars place="tc" message={all || ''} open={!!all} color="danger" />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={{ password1, password2 }}
          validationSchema={validationSchema}
          onSubmit={values => {
            passwordUpdate({ ...values });
          }}
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
          }) => (
            <AuthContext.Consumer>
              {({ id }) => (
                <GridItem className={classes.cardContainer}>
                  <h3>{t('user.change_password')}</h3>
                  <form onSubmit={handleSubmit}>
                    <Card>
                      <Loading show={loading} />
                      <CardBody>
                        <GridContainer direction="column" alignItems="center">
                          <GridItem xs={12} sm={10}>
                            <CustomInput
                              labelText="Nueva contraseña"
                              id="password1"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={
                                touched.password1 && Boolean(errors.password1)
                              }
                              helpText={
                                touched.password1 ? errors.password1 : ''
                              }
                              inputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Lock
                                      className={classes.inputAdornmentIcon}
                                    />
                                  </InputAdornment>
                                ),
                                value: values.password1,
                                onChange: e => {
                                  handleChange(e);
                                },
                                onBlur: handleBlur,
                                type: 'password',
                              }}
                            />
                            <CustomInput
                              labelText="Repetir contraseña"
                              id="password2"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={
                                touched.password2 && Boolean(errors.password2)
                              }
                              helpText={
                                touched.password2 ? errors.password2 : ''
                              }
                              inputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Lock
                                      className={classes.inputAdornmentIcon}
                                    />
                                  </InputAdornment>
                                ),
                                value: values.password2,
                                onChange: e => {
                                  handleChange(e);
                                },
                                onBlur: handleBlur,
                                type: 'password',
                              }}
                            />
                          </GridItem>
                        </GridContainer>
                        <GridContainer direction="column" alignItems="center">
                          <GridItem xs={12} sm={8} md={6}>
                            <div className={classes.editUserFormContainer}>
                              <Button
                                id="user-change-pass-button-save"
                                type="submit"
                                color="primary"
                                block
                              >
                                {t('common.pass.save')}
                              </Button>
                              <Button
                                id="user-change-pass-button-cancel"
                                simple
                                color="danger"
                                block
                                onClick={() => this.handleCancelBtn(id)}
                              >
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </GridItem>
                        </GridContainer>
                      </CardBody>
                    </Card>
                  </form>
                </GridItem>
              )}
            </AuthContext.Consumer>
          )}
        />
      </GridContainer>
    );
  }
}

/**
 * UserChangePassword PropTypes
 * @param classes - prop styles received from withStyles
 * @param errors - prop errors list received from parents
 * @param loading - prop change password mutation status
 * @param passwordUpdate - callback called when validations are success
 */

UserChangePassword.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  passwordUpdate: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(UserChangePassword);
